import { useCallback, useEffect, useRef, useState } from "react";

import type * as monacoEditor from "monaco-editor/esm/vs/editor/editor.api";

import type {
  UseInsertTranslationTagTooltipForMonacoOptions,
  UseInsertTranslationTagTooltipForMonacoReturn,
} from "./types";

/**
 * Shared hook for driving the InsertTranslationTagTooltip from a Monaco selection.
 *
 * Responsibilities:
 * - Show only when a non-empty selection exists and passes `validateSelection`.
 * - Hide while the user is actively dragging a selection (mouse down).
 * - Compute a viewport-based anchor position using Monaco's visible coordinates + the editor's DOM rect.
 * - Dispose Monaco event listeners on cleanup to prevent leaks.
 */
export const useInsertTranslationTagTooltipForMonaco = ({
  enabled,
  validateSelection,
}: UseInsertTranslationTagTooltipForMonacoOptions): UseInsertTranslationTagTooltipForMonacoReturn => {
  const tooltipTargetRef = useRef<HTMLDivElement>(null);
  const validateSelectionRef = useRef(validateSelection);
  validateSelectionRef.current = validateSelection;

  // Track whether the user is actively dragging/selecting. Tooltip should only render on mouse up / stable selection.
  const isMouseDownRef = useRef(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const [editor, setEditor] = useState<monacoEditor.editor.IStandaloneCodeEditor | null>(null);

  const registerEditor = useCallback((nextEditor: monacoEditor.editor.IStandaloneCodeEditor) => {
    setEditor(nextEditor);
  }, []);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const hideTooltip = () => setIsTooltipVisible(false);

    const updateTooltipPositionFromSelection = (selection: monacoEditor.Selection) => {
      const editorDomNode = editor.getDomNode();
      if (!editorDomNode) {
        hideTooltip();
        return;
      }

      // Monaco coordinates are relative to the editor viewport; translate them into page coordinates.
      const startVisiblePosition = editor.getScrolledVisiblePosition(selection.getStartPosition());
      const endVisiblePosition = editor.getScrolledVisiblePosition(selection.getEndPosition());
      if (!startVisiblePosition || !endVisiblePosition) {
        hideTooltip();
        return;
      }

      const editorRect = editorDomNode.getBoundingClientRect();
      setTooltipPosition({
        top: editorRect.top + endVisiblePosition.top + endVisiblePosition.height + 12,
        left: editorRect.left + (startVisiblePosition.left + endVisiblePosition.left) / 2,
      });
      setIsTooltipVisible(true);
    };

    const checkSelectionForTooltip = (selection: monacoEditor.Selection | null | undefined) => {
      // Gate on the caller's feature/channel support logic.
      if (!enabled) {
        hideTooltip();
        return;
      }

      if (!selection || selection.isEmpty()) {
        hideTooltip();
        return;
      }

      if (isMouseDownRef.current) {
        hideTooltip();
        return;
      }

      if (!validateSelectionRef.current(selection, editor)) {
        hideTooltip();
        return;
      }

      updateTooltipPositionFromSelection(selection);
    };

    const onMouseDown = editor.onMouseDown(() => {
      isMouseDownRef.current = true;
      hideTooltip();
    });

    const onMouseUp = editor.onMouseUp(() => {
      isMouseDownRef.current = false;
      checkSelectionForTooltip(editor.getSelection());
    });

    const onCursorSelectionChange = editor.onDidChangeCursorSelection((event) => {
      checkSelectionForTooltip(event.selection);
    });

    // Cleanup: remove Monaco listeners when the editor changes/unmounts.
    return () => {
      onMouseDown.dispose();
      onMouseUp.dispose();
      onCursorSelectionChange.dispose();
    };
  }, [editor, enabled]);

  return {
    tooltipTargetRef,
    shouldRenderTooltip: enabled && isTooltipVisible,
    tooltipPosition,
    registerEditor,
  };
};
